import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { ArrowLeft, Users, UserCheck, UserX, Clock, ExternalLink, Phone, MessageSquare, Search, Download, Edit, Trash2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';

const AdminWeddingDetail = () => {
    const { weddingId } = useParams();
    const navigate = useNavigate();
    const [wedding, setWedding] = useState(null);
    const [guests, setGuests] = useState([]);
    const [families, setFamilies] = useState([]);
    const [publicRSVPs, setPublicRSVPs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchWeddingData = useCallback(async () => {
        try {
            // Note: We might need a specific admin endpoint for this to avoid ownership checks
            // For now, let's assume get_admin_weddings combined with some logic or a new endpoint
            const response = await api.get(`/admin/weddings`);
            const w = response.data.find(x => x.id === weddingId);
            if (!w) {
                toast.error('Boda no encontrada');
                navigate('/admin/weddings');
                return;
            }
            setWedding(w);

            // Fetch guests, families and public RSVPs
            try {
                const [guestsRes, familiesRes, publicRes] = await Promise.all([
                    api.get(`/events/${weddingId}/guests`),
                    api.get(`/events/${weddingId}/families`),
                    api.get(`/events/${weddingId}/public-rsvps`)
                ]);
                setGuests(guestsRes.data);
                setFamilies(familiesRes.data || []);
                setPublicRSVPs(publicRes.data || []);
            } catch (e) {
                console.error('Error fetching data', e);
            }

        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    }, [weddingId, navigate]);

    useEffect(() => {
        fetchWeddingData();
    }, [weddingId, fetchWeddingData]);

    const deleteGuest = async (guestId) => {
        if (!window.confirm('¿Eliminar invitado?')) return;
        try {
            await api.delete(`/events/${weddingId}/guests/${guestId}`);
            toast.success('Invitado eliminado');
            fetchWeddingData();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const stats = React.useMemo(() => {
        const familyCounts = families.reduce((acc, f) => {
            acc[f.id] = guests.filter(g => g.family_id === f.id).length;
            return acc;
        }, {});

        const confirmedGuests = guests.filter(g => g.status === 'confirmed');
        const confirmedGroups = families.filter(f => f.status === 'confirmed' && (familyCounts[f.id] || 0) > 1);
        const confirmedIndividualsFromFamilies = confirmedGuests.filter(g => (familyCounts[g.family_id] || 0) <= 1);
        const confirmedPublic = publicRSVPs.filter(r => r.attending).reduce((acc, r) => acc + (r.num_guests || 1), 0);

        const totalGuests = guests.length + publicRSVPs.reduce((acc, r) => acc + (r.num_guests || 1), 0);

        return {
            totalGuests,
            confirmedInGroups: confirmedGuests.length - confirmedIndividualsFromFamilies.length,
            confirmedIndividuals: confirmedPublic + confirmedIndividualsFromFamilies.length,
            confirmedGroupsCount: confirmedGroups.length,
            declinedCount: guests.filter(g => g.status === 'declined').length + publicRSVPs.filter(r => !r.attending).reduce((acc, r) => acc + (r.num_guests || 1), 0)
        };
    }, [guests, families, publicRSVPs]);

    const filteredGuests = guests.filter(g =>
        `${g.first_name} ${g.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <AdminLayout>Cargando...</AdminLayout>;

    return (
        <AdminLayout>
            <div className="space-y-6">
                <Button variant="ghost" onClick={() => navigate('/admin/weddings')} className="mb-2">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Volver a Bodas
                </Button>

                <div className="grid gap-6 md:grid-cols-3">
                    <Card className="md:col-span-2">
                        <CardHeader>
                            <div className="flex justify-between items-start">
                                <div>
                                    <CardTitle className="text-2xl font-heading text-wedding-primary">{wedding?.title}</CardTitle>
                                    <CardDescription>Detalle administrativo del evento</CardDescription>
                                </div>
                                <Badge variant="outline" className="bg-wedding-primary/5 text-wedding-primary">
                                    {wedding?.design_template}
                                </Badge>
                            </div>
                        </CardHeader>
                        <CardContent className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500 uppercase font-semibold">Propietario</p>
                                <p className="font-medium">{wedding?.user_name}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500 uppercase font-semibold">Fecha</p>
                                <p className="font-medium">{new Date(wedding?.event_date).toLocaleDateString()}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500 uppercase font-semibold">Ubicación</p>
                                <p className="font-medium truncate">{wedding?.venue_name}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500 uppercase font-semibold">Link Público</p>
                                <a href={`/invite/${wedding?.public_token}`} target="_blank" className="text-wedding-primary flex items-center hover:underline">
                                    Ver Invitación <ExternalLink className="h-3 w-3 ml-1" />
                                </a>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Resumen de Asistencia</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between items-center py-2 border-b">
                                <span className="text-slate-600 flex items-center"><Users className="h-4 w-4 mr-2" /> Total Invitados</span>
                                <span className="font-bold">{stats.totalGuests}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b">
                                <span className="text-green-600 flex items-center"><UserCheck className="h-4 w-4 mr-2" /> Invitados en Grupos Confirmados</span>
                                <span className="font-bold text-green-600">{stats.confirmedInGroups}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b">
                                <span className="text-indigo-600 flex items-center"><CheckCircle2 className="h-4 w-4 mr-2" /> Invitados Individuales Confirmados</span>
                                <span className="font-bold text-indigo-600">{stats.confirmedIndividuals}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b">
                                <span className="text-purple-600 flex items-center"><CheckCircle2 className="h-4 w-4 mr-2" /> Grupos Confirmados</span>
                                <span className="font-bold text-purple-600">{stats.confirmedGroupsCount}</span>
                            </div>
                            <div className="flex justify-between items-center py-2 border-b">
                                <span className="text-red-600 flex items-center"><UserX className="h-4 w-4 mr-2" /> No Asisten</span>
                                <span className="font-bold text-red-600">{stats.declinedCount}</span>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>Lista de Invitados</CardTitle>
                        <div className="flex gap-2">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                                <input
                                    type="text"
                                    className="pl-8 pr-2 py-1 text-xs border rounded-md w-48"
                                    placeholder="Buscar invitado..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <Button variant="outline" size="sm"><Download className="h-3 w-3 mr-1" /> Excel</Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nombre</TableHead>
                                    <TableHead>Teléfono</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead>Últ. Movimiento</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredGuests.map((guest) => (
                                    <TableRow key={guest.id}>
                                        <TableCell className="font-medium">{guest.first_name} {guest.last_name}</TableCell>
                                        <TableCell>
                                            <span className="flex items-center text-slate-500 text-sm">
                                                <Phone className="h-3 w-3 mr-1" /> {guest.phone || 'N/A'}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            {guest.status === 'confirmed' ? (
                                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Confirmado</Badge>
                                            ) : guest.status === 'declined' ? (
                                                <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Declinó</Badge>
                                            ) : (
                                                <Badge variant="outline">Pendiente</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-xs text-slate-400">
                                            {new Date(guest.created_at).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600">
                                                    <MessageSquare className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => deleteGuest(guest.id)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default AdminWeddingDetail;
